import { Component, Suspense, lazy, useCallback, useEffect, useState, type ReactNode } from "react";

import TabBar, { type Tab } from "./components/TabBar";
import type { WorkoutFacts } from "./lib/storySlides";
import { onReconnect } from "./lib/offline";
import { isInsideTelegram, setClosingGuard, startParam } from "./lib/telegram";
import { applyTheme, rememberChoice, watchSystemTheme } from "./lib/theme";
import Home from "./screens/Home";
import History from "./screens/History";
import Profile from "./screens/Profile";
import Programs from "./screens/Programs";
import ProgramView from "./screens/ProgramView";
import Session, { SessionSkeleton } from "./screens/Session";
import StartWorkout from "./screens/StartWorkout";
import { useSession } from "./store/session";
import { useUser } from "./store/user";
import DotLoader from "./components/DotLoader";
import PixelField from "./components/PixelField";
import { BACKDROP_LOOK } from "./lib/pixelField";


// Редкие экраны грузятся по требованию (21-retention-plan.md §12): генератор,
// импорт, календарь, настройки и итог-истории не нужны, чтобы открыть главную
// и начать тренировку. Главная, тренировка, программы, история и профиль —
// в основном бандле: это то, что открывают в зале.
const WorkoutStory = lazy(() => import("./components/WorkoutStory"));
const Calendar = lazy(() => import("./screens/Calendar"));
const Generator = lazy(() => import("./screens/Generator"));
const Library = lazy(() => import("./screens/Library"));
const NewProgram = lazy(() => import("./screens/NewProgram"));
const ExerciseStats = lazy(() => import("./screens/ExerciseStats"));
const CoachInvite = lazy(() => import("./screens/CoachInvite"));
const FriendInvite = lazy(() => import("./screens/FriendInvite"));
const PastWorkout = lazy(() => import("./screens/PastWorkout"));
const Friends = lazy(() => import("./screens/Friends"));
const Athletes = lazy(() => import("./screens/Athletes"));
const Maxes = lazy(() => import("./screens/Maxes"));
const Settings = lazy(() => import("./screens/Settings"));
const Templates = lazy(() => import("./screens/Templates"));
const BodyWeightScreen = lazy(() => import("./screens/BodyWeight"));
const SharedProgram = lazy(() => import("./screens/SharedProgram"));

const BOT = "rackgymbot";


/** Приложение живёт внутри Telegram: без initData авторизоваться нечем. */
function OutsideTelegram() {
  return (
    <div className="fallback">
      <div className="wordmark">RACK</div>
      <p className="lead" style={{ margin: 0 }}>
        Приложение открывается только из Telegram — оттуда приходит вход.
      </p>
      <a className="button" href={`https://t.me/${BOT}`}>
        Открыть в Telegram
      </a>
    </div>
  );
}

/**
 * Старт приложения, пока грузится профиль. Здесь ещё нечего показывать
 * формой будущего контента — это первый экран, и он встречает знаком.
 */
function Loading() {
  return (
    <div className="boot">
      <DotLoader size="lg" label="Загрузка" />
      <span className="boot__word">RACK</span>
    </div>
  );
}

/**
 * Редкий экран не догрузился — чаще всего нет связи в зале.
 *
 * Без этого ошибка динамического импорта роняла бы всё приложение в белый
 * экран. Здесь — честная фраза и «Повторить»; тренировка, главная и история
 * лежат в основном бандле и работают и так.
 */
class ChunkBoundary extends Component<{ children: ReactNode; resetKey: string }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidUpdate(prev: { resetKey: string }) {
    // Ушли на другой экран — ошибка прошлого больше не про него.
    if (prev.resetKey !== this.props.resetKey && this.state.failed) this.setState({ failed: false });
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="screen screen--loading">
        <p className="lead" style={{ margin: 0, textAlign: "center" }}>
          Экран не загрузился — похоже, пропала связь.
        </p>
        <button className="button" onClick={() => this.setState({ failed: false })}>
          Повторить
        </button>
      </div>
    );
  }
}

/** Лоадер на время догрузки редкого экрана: доли секунды, но не пустой экран. */
function ScreenLoading() {
  return (
    <div className="screen screen--loading">
      <DotLoader label="Загрузка" />
    </div>
  );
}

/** Экран поверх вкладок. Таб-панель под ним не видна: это отдельный маршрут. */
type Overlay =
  | { kind: "program"; id: number }
  | { kind: "new"; draftId?: string }
  | { kind: "shared"; token: string }
  | { kind: "coach-invite"; token: string }
  | { kind: "friend-invite"; token: string }
  | { kind: "generator" }
  | { kind: "calendar" }
  | { kind: "start" }
  | { kind: "session" }
  | { kind: "past"; clientId: string }
  | { kind: "exercise"; exerciseId: number; from: Overlay }
  | { kind: "library" }
  // Разделы профиля: за каждым свой экран, а не карточка в общей простыне.
  | { kind: "friends" }
  | { kind: "athletes" }
  | { kind: "maxes" }
  | { kind: "settings" }
  // Галерея готовых программ (21-retention-plan.md §4).
  | { kind: "templates" }
  | { kind: "body" }
  | null;

export default function App() {
  const { me, status, error, load } = useUser();
  const { active, restore, start, sync } = useSession();
  // Навигация состоянием, а не роутером: маршрутов немного, а синхронизация с
  // историей Telegram — источник багов на ровном месте.
  const [tab, setTab] = useState<Tab>("home");
  const [overlay, setOverlay] = useState<Overlay>(null);
  // Итог тренировки живёт здесь, а не на экране сессии: завершение чистит
  // стор, и экран размонтируется раньше, чем успел бы что-то показать.
  const [finished, setFinished] = useState<{ facts: WorkoutFacts; clientId: string } | null>(null);

  useEffect(() => {
    if (!isInsideTelegram()) return;
    void load();
    // Тренировка, начатая до перезагрузки, должна вернуться сама (§5 ТЗ).
    void restore();
  }, [load, restore]);

  // Связь вернулась — досылаем накопленное. Подписка живёт на уровне
  // приложения: очередь не привязана к тому, какой экран сейчас открыт.
  useEffect(() => onReconnect(() => void sync()), [sync]);

  // Бот разобрал программу, но не всё узнал, и прислал кнопку с ?draft=<id>:
  // открываем экран проверки этого черновика. Параметр убираем из адреса,
  // чтобы перезагрузка не открыла его снова.
  useEffect(() => {
    if (status !== "ready") return;
    // Ссылка «поделиться»: t.me/<бот>?startapp=p_<токен>.
    const start = startParam();
    if (start?.startsWith("p_")) {
      setTab("programs");
      setOverlay({ kind: "shared", token: start.slice(2) });
      return;
    }
    // Приглашение тренера: t.me/<бот>?startapp=c_<токен>. Открываем экран
    // согласия — до него тренер не видит ничего (14-coach-plan.md).
    if (start?.startsWith("c_")) {
      setTab("profile");
      setOverlay({ kind: "coach-invite", token: start.slice(2) });
      return;
    }
    // Приглашение в друзья: t.me/<бот>?startapp=f_<токен>.
    if (start?.startsWith("f_")) {
      setTab("profile");
      setOverlay({ kind: "friend-invite", token: start.slice(2) });
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const draftId = params.get("draft");
    if (!draftId) return;
    params.delete("draft");
    const rest = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : ""));
    setTab("programs");
    setOverlay({ kind: "new", draftId });
  }, [status]);

  useEffect(() => setClosingGuard(Boolean(active)), [active]);

  // Тема приехала с профилем: до этого экран покрашен по кэшу прошлого запуска.
  // Кэш обновляем здесь же — со следующего старта вспышки не будет.
  const choice = me?.theme;
  useEffect(() => {
    if (!choice) return;
    applyTheme(choice);
    rememberChoice(choice);
    // Выбрана «системная» — идём за телефоном и дальше: человек переключает
    // ночной режим, и приложение обязано переключиться вместе с ним.
    if (choice !== "system") return;
    return watchSystemTheme(() => applyTheme(choice));
  }, [choice]);

  // Стабильная ссылка: история подписывает её на нативную «назад».
  const closeStory = useCallback(() => setFinished(null), []);

  if (!isInsideTelegram()) return <OutsideTelegram />;
  if (status === "loading") return <Loading />;

  if (status === "error") {
    return (
      <div className="fallback">
        <div className="wordmark wordmark--muted">Не вышло</div>
        <p className="lead" style={{ margin: 0 }}>
          Бэкенд не ответил. {error}
        </p>
        <button className="button" onClick={() => void load()}>
          Попробовать снова
        </button>
      </div>
    );
  }

  /** Центральная вкладка: идущую тренировку продолжаем, иначе выбираем день. */
  function openWorkout() {
    setTab("workout");
    setOverlay(active ? { kind: "session" } : { kind: "start" });
  }

  async function begin(dayId: number | null) {
    const session = await start(dayId);
    // Не начали — остаёмся там, где нажали: там же видна причина. Раньше сбой
    // молча выкидывал в «Программы», и казалось, что кнопка не работает.
    if (session) setOverlay({ kind: "session" });
  }

  const screen = () => {
    if (overlay?.kind === "session") {
      // Завершение чистит стор раньше, чем экран успевает сменить вкладку.
      // Без этой ветки в зазор между двумя рендерами мелькает список программ.
      if (!active) return <SessionSkeleton />;
      return (
        <Session
          onLeave={() => {
            setOverlay(null);
            setTab("programs");
          }}
          onFinished={(facts, clientId) => {
            setOverlay(null);
            setTab("history");
            setFinished({ facts, clientId });
          }}
        />
      );
    }
    if (overlay?.kind === "shared") {
      return (
        <SharedProgram
          token={overlay.token}
          onBack={() => {
            setOverlay(null);
            setTab("programs");
          }}
          onOpenProgram={(id) => setOverlay({ kind: "program", id })}
        />
      );
    }
    if (overlay?.kind === "coach-invite") {
      return (
        <CoachInvite
          token={overlay.token}
          onBack={() => {
            setOverlay(null);
            setTab("profile");
          }}
          onDone={() => {
            setOverlay(null);
            setTab("home");
          }}
        />
      );
    }
    if (overlay?.kind === "friend-invite") {
      return (
        <FriendInvite
          token={overlay.token}
          onBack={() => {
            setOverlay(null);
            setTab("profile");
          }}
          onDone={() => setOverlay(null)}
        />
      );
    }
    if (overlay?.kind === "program") {
      return (
        <ProgramView
          key={overlay.id}
          programId={overlay.id}
          onOpenProgram={(id) => setOverlay({ kind: "program", id })}
          onBack={() => setOverlay(null)}
          onStartDay={(dayId) => void begin(dayId)}
        />
      );
    }
    if (overlay?.kind === "generator") {
      return (
        <Generator
          onOpenTemplates={() => setOverlay({ kind: "templates" })}
          onBack={() => setOverlay({ kind: "new" })}
          onCreated={(id) => setOverlay({ kind: "program", id })}
        />
      );
    }
    if (overlay?.kind === "new") {
      return (
        <NewProgram
          key={overlay.draftId ?? "new"}
          draftId={overlay.draftId}
          onHistoryImported={() => {
            setOverlay(null);
            setTab("history");
          }}
          onBack={() => setOverlay(null)}
          onGenerator={() => setOverlay({ kind: "generator" })}
          onCreated={(id) => setOverlay({ kind: "program", id })}
        />
      );
    }
    if (overlay?.kind === "start") {
      return (
        <StartWorkout
          onPickDay={(dayId) => void begin(dayId)}
          onFreeWorkout={() => void begin(null)}
          onBack={() => {
            setOverlay(null);
            setTab("programs");
          }}
        />
      );
    }

    if (overlay?.kind === "exercise") {
      // Возвращаемся туда, откуда пришли: из библиотеки — в библиотеку,
      // из прошедшей тренировки — обратно в неё, а не в корень вкладки.
      const back = overlay.from;
      return (
        <ExerciseStats
          exerciseId={overlay.exerciseId}
          onBack={() => setOverlay(back)}
        />
      );
    }

    if (overlay?.kind === "calendar") {
      return <Calendar onStartDay={(dayId) => void begin(dayId)} />;
    }

    if (overlay?.kind === "friends") {
      return <Friends onBack={() => setOverlay(null)} />;
    }

    if (overlay?.kind === "athletes") {
      return (
        <Athletes
          onBack={() => setOverlay(null)}
          onOpenWorkout={(clientId) => setOverlay({ kind: "past", clientId })}
        />
      );
    }

    if (overlay?.kind === "maxes") {
      return <Maxes onBack={() => setOverlay(null)} />;
    }

    if (overlay?.kind === "templates") {
      return (
        <Templates
          onBack={() => setOverlay(null)}
          onStarted={() => {
            setOverlay(null);
            setTab("home");
          }}
          onAdded={(id) => setOverlay({ kind: "program", id })}
        />
      );
    }

    if (overlay?.kind === "body") {
      return <BodyWeightScreen onBack={() => setOverlay(null)} />;
    }

    if (overlay?.kind === "settings") {
      return <Settings onBack={() => setOverlay(null)} />;
    }

    if (overlay?.kind === "library") {
      const here = overlay;
      return (
        <Library
          onBack={() => setOverlay(null)}
          onOpen={(exerciseId) =>
            setOverlay({ kind: "exercise", exerciseId, from: here })
          }
        />
      );
    }

    if (overlay?.kind === "past") {
      const here = overlay;
      return (
        <PastWorkout
          clientId={overlay.clientId}
          onOpenProgram={(id) => setOverlay({ kind: "program", id })}
          onBack={() => {
            setOverlay(null);
            setTab("history");
          }}
          onOpenExercise={(exerciseId) =>
            setOverlay({ kind: "exercise", exerciseId, from: here })
          }
        />
      );
    }

    switch (tab) {
      case "history":
        return (
          <History
            onStart={openWorkout}
            onOpen={(clientId) => setOverlay({ kind: "past", clientId })}
          />
        );
      case "home":
        return (
          <Home
            onStartDay={(dayId) => void begin(dayId)}
            onContinue={() => setOverlay({ kind: "session" })}
            onOpenSession={(clientId) => setOverlay({ kind: "past", clientId })}
            onOpenExercise={(exerciseId) =>
              setOverlay({ kind: "exercise", exerciseId, from: null })
            }
            onOpenPrograms={() => setTab("programs")}
            onOpenCalendar={() => setOverlay({ kind: "calendar" })}
            onOpenTemplates={() => setOverlay({ kind: "templates" })}
          />
        );
      case "profile":
        return (
          <Profile
            onOpenExercise={(exerciseId) =>
              setOverlay({ kind: "exercise", exerciseId, from: null })
            }
            onOpenFriends={() => setOverlay({ kind: "friends" })}
            onOpenAthletes={() => setOverlay({ kind: "athletes" })}
            onOpenMaxes={() => setOverlay({ kind: "maxes" })}
            onOpenSettings={() => setOverlay({ kind: "settings" })}
            onOpenCalendar={() => setOverlay({ kind: "calendar" })}
            onOpenBody={() => setOverlay({ kind: "body" })}
          />
        );
      case "programs":
      default:
        return (
          <Programs
            userName={me?.first_name?.trim() || me?.username || "спортсмен"}
            onOpen={(id) => setOverlay({ kind: "program", id })}
            onCreate={() => setOverlay({ kind: "new" })}
            onOpenLibrary={() => setOverlay({ kind: "library" })}
            onOpenTemplates={() => setOverlay({ kind: "templates" })}
          />
        );
    }
  };

  return (
    // На экране тренировки таб-панели нет: разделы там не нужны, а 68 пикселей
    // нужны таблице подходов. Уйти можно стрелкой «свернуть» в шапке.
    <div className={`app ${overlay?.kind === "session" ? "app--focus" : ""}`}>
      {/* На экране тренировки фона нет: там чтение цифр и отклик важнее (§12 ТЗ). */}
      {overlay?.kind !== "session" && (
        <PixelField className="app__backdrop" seed={9} fps={15} look={BACKDROP_LOOK} />
      )}
      {/* Пока редкий экран догружается — точечный лоадер, а не пустота. */}
      <div className="app__body">
        <ChunkBoundary resetKey={`${tab}:${overlay?.kind ?? ""}`}>
          <Suspense fallback={<ScreenLoading />}>{screen()}</Suspense>
        </ChunkBoundary>
      </div>
      {finished && (
        <Suspense fallback={null}>
          <WorkoutStory
            facts={finished.facts}
            shareClientId={finished.clientId}
            onClose={closeStory}
          />
        </Suspense>
      )}
      {overlay?.kind !== "session" && (
        <TabBar
          active={tab}
          workoutActive={Boolean(active)}
          onChange={(next) => {
            if (next === "workout") {
              openWorkout();
              return;
            }
            setOverlay(null);
            setTab(next);
          }}
          onReselect={(same) => {
            if (same === "workout") {
              openWorkout();
              return;
            }
            // Поверх раздела что-то открыто (друзья, параметры) — закрываем.
            // Открыт сам раздел — возвращаемся к его началу.
            if (overlay) {
              setOverlay(null);
              return;
            }
            document.querySelector(".app__body")?.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}
    </div>
  );
}
